import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from '@/app/app';
import { installAuth } from '@/core/auth/install-auth';
import { setLeaveProtectedUiHandler } from '@/core/auth/leave-protected-ui';
import { profileWithPermissions } from '@/core/auth/permissions-from-identity';
import { restoreSession } from '@/core/auth/session-manager';
import { seedAbility } from '@/core/authz/ability-provider';
import { env } from '@/core/env/env';
import { RootErrorBoundary } from '@/core/errors/root-error-boundary';
import { DEFAULT_LOCALE, initI18n, isSupportedLocale } from '@/core/i18n';
import { applyDocumentDirection, readStoredLocale } from '@/core/i18n/direction';
import { initSentry } from '@/core/observability/sentry';
import '@/styles/globals.css';

// Synchronous and side-effect-only (see initSentry's doc); not part of the boot order below,
// so must not be awaited into it.
initSentry();

// Boot order is deliberate: direction and i18n resolve before first paint, mocks intercept before
// the first request, and the session settles before the router is constructed — that last step
// alone removes the login flash, the guard-vs-refresh race, and the boot thundering herd, since no
// protected route can render before a token exists.
async function bootstrap(): Promise<void> {
  const stored = readStoredLocale();
  const locale = stored && isSupportedLocale(stored) ? stored : DEFAULT_LOCALE;
  applyDocumentDirection(locale);
  await initI18n(locale);

  // Dynamic import so the worker is a separate chunk: `env.VITE_ENABLE_MOCKS` is a validated
  // property read Vite cannot fold away, so this is a runtime branch, not dead-code
  // elimination — `vite.config.ts` refusing to build with mocks enabled is the actual guarantee.
  if (env.VITE_ENABLE_MOCKS) {
    const { worker } = await import('@/mocks/browser');
    await worker.start({ onUnhandledRequest: 'bypass' });
  }

  // Started here but awaited below: fetching the module can overlap the profile request, so
  // only constructing the router (not downloading it) needs to wait.
  const routerModule = import('@/app/router');

  // Interceptors before the session resolves: `profileWithPermissions` reads the caller's roles
  // and the role definitions from the identity store, and those requests — like any protected
  // query fired right after boot — must carry a bearer token and be able to refresh on 401.
  installAuth();
  await restoreSession(profileWithPermissions);

  // Seeds the ability from the settled session before the router is constructed: the initial
  // navigation's ability loaders run before the provider mounts, so a later provider-time sync
  // would let a hard load read the deny-all seed. Ongoing sync is the provider's subscription.
  seedAbility();

  // Constructed only now, so it reads the URL after the OAuth params were stripped.
  const router = (await routerModule).createAppRouter();
  // An involuntary session end goes to /auth/signed-out, never /auth/login: the latter would
  // redirect to Hosted UI, where a still-live session cookie silently signs the user back in.
  setLeaveProtectedUiHandler(() => void router.navigate('/auth/signed-out', { replace: true }));

  const container = document.getElementById('root');
  if (!container) throw new Error('Root container #root not found in index.html');

  createRoot(container).render(
    <StrictMode>
      <RootErrorBoundary>
        <App router={router} />
      </RootErrorBoundary>
    </StrictMode>,
  );
}

void bootstrap();
